import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import CancelOrderModal from '@/components/Modals/CancelOrderModal/CancelOrderModal';
import Modal from '@/components/Modals/Modal';
import { COUNTRIES_WITH_CANCEL_POSSIBILITY } from '@/constants/countries';
import useResponsive from '@/hooks/useResponsive';
import { RootState } from '@/redux/store';
import { Data } from '@/types/fulfill/statusOrders';
import { IResOrderData } from '@/types/parcel/orders/orderById.interface';

import { ActionButtons, LocationDetails, MapSection, OrderInfo, PriceSection } from './components';
import {
  ActionButtonsSkeleton,
  LocationDetailsSkeleton,
  OrderInfoSkeleton,
  PriceSectionSkeleton,
} from './components/Skeletons';
import useDetailsModal from './useDetailsModal';
import { formatDataFF } from './utils/formatDataFF';

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  map?: boolean;
  orderDetails?: IResOrderData | null | Data;
  cancel?: boolean;
  isPickups?: boolean;
  isFFOrder?: boolean;
  onOrderCancelled?: () => void;
  isLoading?: boolean;
  showActivityText?: string;
  hideActivityText?: string;
}

const DetailsModal = ({
  isOpen,
  onClose,
  map,
  orderDetails,
  isPickups,
  isFFOrder,
  onOrderCancelled,
  isLoading = false,
}: DetailsModalProps) => {
  const isTablet = useResponsive('up', 'lg');
  const { profile } = useSelector((state: RootState) => state.parcel);
  const country = profile.data?.country;

  const withCancelPossibility = !!country && COUNTRIES_WITH_CANCEL_POSSIBILITY.includes(country);

  const { orderData, orderEvents } = useMemo(() => {
    if (!orderDetails) return { orderData: undefined, orderEvents: undefined };

    if ('orderData' in orderDetails) {
      return {
        orderData: orderDetails.orderData,
        orderEvents: orderDetails.orderEvents,
      };
    }

    const formattedData = formatDataFF(orderDetails);

    const ffEvents =
      orderDetails.items?.map((item) => ({
        name: item.description || '',
        quantity: item.quantity ?? 1,
        sku: item.sku || '',
      })) || [];

    return {
      orderData: formattedData,
      orderEvents: ffEvents,
    };
  }, [orderDetails]);

  const hook = useDetailsModal(
    onClose,
    orderData?.trackingId,
    isPickups,
    onOrderCancelled,
    orderDetails,
  );

  return (
    <Modal isOpen={isOpen} title="Detalle de orden" onClose={onClose} maxWidth="xl">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isTablet ? '0' : '0 !important',
        }}
      >
        {isLoading ? (
          <OrderInfoSkeleton />
        ) : (
          <OrderInfo
            orderData={orderData}
            isFFOrder={isFFOrder}
            isPickups={isPickups}
            copyCode={hook.copyCode}
            popoverOpen={hook.popoverOpen}
            idCopyCode={hook.idCopyCode}
            copy={hook.copy}
          />
        )}

        <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: isTablet ? 'row' : 'column',
            alignItems: isTablet ? 'start' : 'center',
            width: '100%',
            gap: '2rem',
          }}
        >
          <MapSection map={map} mapLoading={hook.map.loading} mapUrl={hook.map.mapUrl} />

          <Box
            sx={{
              width: !isTablet ? 1 : map ? 0.7 : 1,
            }}
          >
            {isLoading ? (
              <LocationDetailsSkeleton />
            ) : (
              <LocationDetails
                orderData={orderData}
                orderEvents={orderEvents}
                isFFOrder={isFFOrder}
                orderId={orderData?.trackingId}
                handleActivityIcon={hook.handleActivityIcon}
              />
            )}
          </Box>
        </Box>

        <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: isTablet ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: '1rem',
          }}
        >
          {isLoading ? (
            <>
              <PriceSectionSkeleton />
              <ActionButtonsSkeleton />
            </>
          ) : (
            <>
              <PriceSection
                orderData={orderData}
                isFFOrder={isFFOrder}
                countriesWithOutPrices={hook.countriesWithOutPrices}
                country={hook.country}
              />

              <ActionButtons
                isCancelActive={hook.isCancelActive}
                orderStatusNameES={orderData?.statusNameES}
                orderStatusCode={orderData?.status}
                withCancelPossibility={withCancelPossibility}
                handleOpenCancelOrderModal={hook.handleOpenCancelOrderModal}
                handleOpenDownloadModal={hook.handleOpenDownloadModal}
                isOrderDelivered={hook.isOrderDelivered}
              />
            </>
          )}
        </Box>
      </Box>

      <CancelOrderModal
        isOpen={hook.cancelOrderModal}
        onClose={hook.handleCloseCancelOrderModal}
        onConfirm={(reason: string) => {
          if (orderData?.trackingId) {
            hook.handleCancelOrder({
              trackingId: orderData.trackingId,
              reason: reason,
            });
          }
        }}
      />
    </Modal>
  );
};

export default DetailsModal;
